import { Play, SquareTerminal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { Button, Dialog, EmptyState, Input, Select, shortPath, toast, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { runAction } from '../../lib/api.ts';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { useOrgus } from '../../lib/store.ts';
import { closeRunCommand, commandError, openRunCommand, useRunCommand } from './store.ts';

/** Mounted once in the shell: the Run command dialog and its palette entry. */
export function RunCommandHost() {
  const cmds = useMemo<Command[]>(
    () => [
      {
        id: 'job.run',
        title: 'Run command',
        subtitle: 'Run a shell command in a checkout, with live output',
        group: 'Actions',
        icon: SquareTerminal,
        keywords: ['job', 'shell', 'terminal', 'npm', 'pnpm', 'test', 'build'],
        order: 30,
        perform: () => openRunCommand(),
      },
    ],
    [],
  );
  useRegisterCommands(cmds, [cmds]);
  const open = useRunCommand((s) => s.open);
  return open ? <RunCommandDialog /> : null;
}

function RunCommandDialog() {
  const prefill = useRunCommand((s) => s.prefill);
  const navigate = useNavigate();
  const { checkouts, machines, projects } = useOrgus(useShallow((s) => ({ checkouts: s.checkouts, machines: s.machines, projects: s.projects })));
  const options = useMemo(
    () =>
      Object.values(checkouts)
        .filter((c) => machines[c.machineId]?.online && !c.error)
        .sort((a, b) => (projects[a.projectId]?.name ?? '').localeCompare(projects[b.projectId]?.name ?? '') || Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path))
        .map((c) => ({
          value: c.id,
          label: `${projects[c.projectId]?.name ?? 'Project'} · ${shortPath(c.path, 42)}${c.branch ? ` (${c.branch})` : ''} · ${machines[c.machineId]?.name ?? 'machine'}`,
        })),
    [checkouts, machines, projects],
  );
  const initial =
    prefill.checkoutId && options.some((o) => o.value === prefill.checkoutId)
      ? prefill.checkoutId
      : (options.find((o) => prefill.projectId && checkouts[o.value]?.projectId === prefill.projectId)?.value ??
        options.find((o) => prefill.machineId && checkouts[o.value]?.machineId === prefill.machineId)?.value ??
        options[0]?.value ??
        '');
  const [checkoutId, setCheckoutId] = useState(initial);
  const [command, setCommand] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => setError(null), [checkoutId, command]);

  const invalid = commandError(command);
  const checkout = checkouts[checkoutId];

  const run = async () => {
    setTouched(true);
    if (invalid || !checkout) return;
    setBusy(true);
    try {
      const res = await runAction(checkout.machineId, 'job.run', { checkoutId: checkout.id, command: command.trim() });
      const jobId = (res.data as { jobId?: string } | undefined)?.jobId;
      closeRunCommand();
      toast.success('Command started', command.trim());
      if (jobId) navigate(`/machines?machine=${encodeURIComponent(checkout.machineId)}&job=${encodeURIComponent(jobId)}`);
    } catch (e) {
      setError(toUiError(e, { what: 'The command did not start.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && !busy && closeRunCommand()}
      size="md"
      title="Run command"
      dismissible={!busy}
      footer={
        options.length ? (
          <>
            <Button variant="ghost" onClick={closeRunCommand} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" icon={<Play />} loading={busy} onClick={run}>
              Run command
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={closeRunCommand}>
            Close
          </Button>
        )
      }
    >
      {options.length ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void run();
          }}
        >
          <Select label="Where" options={options} value={checkoutId} onValueChange={setCheckoutId} />
          <Input
            label="Command"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="pnpm test"
            autoFocus
            spellCheck={false}
            className="font-mono"
            error={touched && invalid ? invalid : undefined}
            hint="Runs through the machine's shell, as you would type it in a terminal there."
          />
          {error && (
            <div role="alert" className="rounded-md border border-failed/30 bg-failed-soft px-3 py-2 text-base">
              <p className="font-medium text-fg">{error.what}</p>
              <p className="text-fg-muted">{error.why}</p>
              <p className="text-fg-muted">{error.next}</p>
            </div>
          )}
        </form>
      ) : (
        <EmptyState
          icon={<SquareTerminal />}
          title="No checkout to run in"
          description="Add a project, or start the agent on its machine."
        />
      )}
    </Dialog>
  );
}
